<template>
  <template v-if="restriction === 'Hide' && !isEnabled">
    <!-- Скрываем для всех ролей, включая Support, когда услуга отключена -->
  </template>
  <template v-else-if="role === UserRoles.SUPPORT">
    <slot />
  </template>
  <template v-else-if="!(!isEnabled && restriction === 'Disabled')">
    <slot :disabled="(!isEnabled && restriction === 'Disabled')" />
  </template>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, computed, watch, onMounted } from 'vue'

import { useUserData, UserFields } from '@/shared/composables/local-storage'
import { useFeatureAccessEmulatorStore, useUserFeatures } from '@/shared/composables/user-features'
import { UserRoles } from '@/shared/types/router'
import { firstLetterToUpperCaseHandler } from '@/shared/utils/string'

import { componentsFeaturesMap, FeatureableComponents, restrictionsMap, UserFeaturesComponentRestrictions } from './types'

const props = defineProps<{
  componentName: FeatureableComponents,
}>()

const { getField } = useUserData()
const { enabledFeatures, hasFeature, isChatMessagingUiEnabled } = useUserFeatures()
const featureAccessEmulator = useFeatureAccessEmulatorStore()
const {
  isActive,
  webCallBackorder,
  webToFax,
  forward,
  blackWhiteList,
  voiceMailBoxAccess,
  instantMessaging,
  cdrsAccess,
  webSoftphone,
  accessGroupManagement,
  doNotDisturb,
} = storeToRefs(featureAccessEmulator)

/** Настройки чата — только полный UC (softphone ∧ IM). Тема живёт в персональных настройках. */
const requiresChatMessagingUi = (name: string) =>
  name === 'ChatSettings'

const role = getField(UserFields.USER_ROLE)

const componentName = computed(() => firstLetterToUpperCaseHandler(props.componentName))

const restriction = computed<UserFeaturesComponentRestrictions | undefined>(() => restrictionsMap.get(componentName.value))

const isEnabled = ref(true)
const onFeaturesChange = () => {
  if (!componentsFeaturesMap.has(componentName.value)) {
    return
  }

  const features = componentsFeaturesMap.get(componentName.value)
  if (!features) {
    return
  }

  let enabled = true
  for (const feature of features) {
    if (!hasFeature(feature)) {
      enabled = false
      break
    }
  }

  if (enabled && requiresChatMessagingUi(componentName.value)) {
    enabled = isChatMessagingUiEnabled.value
  }

  isEnabled.value = enabled
}

watch(
  [
    enabledFeatures,
    isChatMessagingUiEnabled,
    isActive,
    webCallBackorder,
    webToFax,
    forward,
    blackWhiteList,
    voiceMailBoxAccess,
    instantMessaging,
    cdrsAccess,
    webSoftphone,
    accessGroupManagement,
    doNotDisturb,
  ],
  onFeaturesChange,
)
onMounted(() => {
  onFeaturesChange()
})
</script>
